import { useCustomerAPI } from '../../clients/customer-api/customer-api-client';
import { useContext, useEffect, useState } from 'react';
import { addNumericSuffix } from '../../util/util';
import { Info } from 'luxon';
import { DurationUnit, RecurringType, RentalProductPrice } from '../../clients/quipli-service/pricing/pricing';
import { filter, keyBy, partition, sortBy } from 'lodash';
import './product-pricing-table.scss';
import { OrderType } from '../../clients/quipli-service/order/order';
import { CustomerContext } from '../../context/customer-context';
import { useTagDiscount } from '../hooks/use-tag-discount';
import { CustomerProduct } from '../../clients/customer-api/product/customer-product';

export const ProductPricingTable = ({
  productId,
  locationId,
  userId,
  showRangePriceUnitsConf,
  customerProduct,
}: {
  productId: number;
  locationId?: number;
  userId?: number;
  showRangePriceUnitsConf?: boolean;
  customerProduct?: CustomerProduct;
}) => {
  const { listPricesForMultipleProducts, getConfigurations } = useCustomerAPI();
  const [thePrices, setThePrices] = useState<RentalProductPrice[]>([]);
  const [featuredPrices, setFeaturedPrices] = useState<
    { price: number; priceLabel: string; durationLabel: string; activeOn: string; featured: boolean }[]
  >([]);
  const [showRangePriceUnits, setShowRangePriceUnits] = useState<boolean | undefined>(showRangePriceUnitsConf);
  const [taxInclusivePricing, setTaxInclusivePricing] = useState<boolean>(false);
  const [taxLabel, setTaxLabel] = useState<string | undefined>(undefined);
  const customer = useContext(CustomerContext);
  const currencyFormat = Intl.NumberFormat('us-en', { currency: 'USD', minimumFractionDigits: 2 });
  const translateDaysOfWeek = (dow: number[]): string =>
    dow.length === 7 ? 'week' : dow.map((day) => Info.weekdays('short')[day - 1]).join(', ');
  const translateDaysOfMonth = (daysOfMonth: number[]): string => {
    return daysOfMonth.map((day) => (day === -1 ? 'last' : addNumericSuffix(day))).join(', ');
  };
  const translateWeeksOfMonth = (wom: number[]): string =>
    wom.map((week) => (week === -1 ? 'last' : addNumericSuffix(week))).join(', ');

  useEffect(() => {
    getConfigurations(['CONF_SHOW_RANGE_PRICE_UNITS', 'CONF_SHOW_PRICES_WITH_TAX', 'CONF_PRICE_TAX_LABEL']).then(
      (confs) => {
        if (showRangePriceUnitsConf === undefined) {
          setShowRangePriceUnits(confs.find((conf) => conf.variable == 'CONF_SHOW_RANGE_PRICE_UNITS')?.value === '1');
        } else setShowRangePriceUnits(showRangePriceUnitsConf);
        setTaxInclusivePricing(confs.find((conf) => conf.variable == 'CONF_SHOW_PRICES_WITH_TAX')?.value === '1');
        setTaxLabel(confs.find((conf) => conf.variable == 'CONF_PRICE_TAX_LABEL')?.value);
      },
    );
  }, []);

  const { tagDiscount } = useTagDiscount(customerProduct);

  useEffect(() => {
    const allPrices: RentalProductPrice[] = [];
    const [locationPrices, globalPrices] = partition(
      thePrices.filter((p) => p.durationUnit !== DurationUnit.minute),
      (p) => (p.locationIds ?? []).length > 0,
    );
    // have location prices override global prices
    const rentalProductPrices = keyBy(
      locationPrices,
      (p) =>
        `${p.durationValue}/${p.durationUnit}/${p.recurringType}/${p.recurringDaysOfWeek}/${p.recurringDaysOfMonth}/${p.recurringWeeksOfMonth}`,
    );
    globalPrices.forEach((p) => {
      const key = `${p.durationValue}/${p.durationUnit}/${p.recurringType}/${p.recurringDaysOfWeek}/${p.recurringDaysOfMonth}/${p.recurringWeeksOfMonth}`;
      const existing = rentalProductPrices[key];
      if (!existing) rentalProductPrices[key] = p;
      else if (existing && existing.from && existing.to) allPrices.push(p); // existing location price has a date range, so still show global price too
    });

    for (const k in rentalProductPrices) {
      allPrices.push(rentalProductPrices[k]);
    }
    const formattedPrices = filter(allPrices, (rpp) => rpp.featured).map((rpp) => {
      const fromFormatted = rpp.from?.toFormat('LLLL d, y');
      const toFormatted = rpp.to?.toFormat('LLLL d, y');
      const activeOn =
        rpp.recurringType === RecurringType.Weekly
          ? `Every ${translateDaysOfWeek(rpp.recurringDaysOfWeek!)}`
          : rpp.recurringType === RecurringType.Biweekly
          ? `Every other ${translateDaysOfWeek(rpp.recurringDaysOfWeek!)}`
          : rpp.recurringType === RecurringType.DateMonthly
          ? `Every ${translateDaysOfMonth(rpp.recurringDaysOfMonth!)} of the month`
          : rpp.recurringType === RecurringType.DayOfWeekMonthly
          ? `Every ${translateWeeksOfMonth(rpp.recurringWeeksOfMonth!)} ${translateDaysOfWeek(
              rpp.recurringDaysOfWeek!,
            )} of the month`
          : rpp.from && rpp.to
          ? `${fromFormatted}${fromFormatted === toFormatted ? '' : ` - ${rpp.to.toFormat('LLLL d, y')}`}`
          : 'Every day';
      const duration =
        rpp.durationLabel ??
        `${rpp.durationValue === 1 ? '' : `${rpp.durationValue} `}${rpp.durationUnit}${
          rpp.durationValue > 1 ? 's' : ''
        }`;
      const taxPct = taxInclusivePricing ? 1 + (rpp.taxPercentage ? rpp.taxPercentage : 0) / 100 : 1;
      return {
        priceLabel: `$${currencyFormat.format((rpp.price * tagDiscount * taxPct) / 100)}${
          rpp.featured ? '' : ` per ${duration}`
        }`,
        durationLabel: showRangePriceUnits ? duration : `Per ${duration}`,
        activeOn,
        featured: rpp.featured,
        price: rpp.price * tagDiscount * (taxInclusivePricing ? 1 + (rpp.taxPercentage ?? 0) / 100 : 1),
        durationMinutes: rpp.durationMinutes,
      };
    });
    setFeaturedPrices(sortBy(formattedPrices, (f) => f.durationMinutes));
  }, [thePrices]);

  useEffect(() => {
    listPricesForMultipleProducts([productId], locationId, OrderType.Rental, userId).then((p) => {
      setThePrices((p.get(productId) || []) as RentalProductPrice[]);
    });
  }, [productId, locationId]);

  return (
    <div className="product-pricing-table font-sans">
      {taxInclusivePricing &&
      (customer ? customer.chargeTax : true) &&
      thePrices.some((_) => _.taxPercentage && _.taxPercentage > 0) ? (
        <span className="text-xs text-gray-600">All prices include {taxLabel ?? 'tax'}</span>
      ) : (
        <></>
      )}
      <div className="auto-cols-auto border font-bold border-solid border-gray-200 rounded flex align-center py-4 mb-6 flex-wrap justify-center items-center">
        {[...featuredPrices].map((fp, i) => {
          return (
            <div
              key={i}
              className="items-center flex grow max-w-[50%] flex-col border border-solid  border-gray-200 border-l-0 border-y-0 border-r last-of-type:border-r-0"
            >
              <span className="text-[10px] text-gray-600 mb-1">{fp.durationLabel}</span>
              <span>{fp.priceLabel}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
